/* CV reading focus — the entry at the reading line stays at full strength and the
   rest fade out with their distance from it, so scrolling walks a spotlight down
   the list. Pure opacity, no layout, so nothing shifts while it runs. */
function cvFocus() {
    const entries = Array.from(
        document.querySelectorAll('.gh-cv-sections .gh-cv-entry')
    );

    if (!entries.length) return;

    const scrollport = document.querySelector('[data-scrollport]');
    if (!scrollport) return;

    /* Distance from the reading line, as a share of the visible height, at which an entry
       is as pale as it gets. Only the entry sitting on the line reaches full
       strength: a plateau around it, or a curve that starts flat, puts several
       entries in the foreground at once. */
    const FADE_RATIO = 0.45;
    /* The design greys entries to #c9c9c9. Over white, this is the share of
       --portfolio-text (#15171a) that lands there. */
    const FADED_OPACITY = 0.233;
    /* Share of the visible height an entry dissolves over as it reaches the top
       or bottom of the reading area. Text cut off at a hard edge is what makes a
       list read as a box being scrolled; text that thins out into the page reads
       as the text itself moving, with no box to see. */
    const EDGE_RATIO = 0.18;
    /* Quiet before the list settles. This is the pause between the scroll
       stopping and the list coming to rest, so it stays short: long enough to
       sit out the gap between two wheel notches, and no longer. */
    const SETTLE_DELAY = 70;
    /* The run to the entry. Around what the browser's own smooth scroll takes,
       which was the right pace; what made that one read as a jerk was setting
       off at speed, and the easing rather than the duration answers that. */
    const SETTLE_DURATION = 300;
    /* Nearer than this and there is nothing to correct. Without it the settle
       would answer its own scrolling and walk the list an entry at a time. */
    const SETTLE_EPSILON = 1.5;
    /* Wheel travel an entry holds on to before the list moves on. Just under the
       100 or so a mouse notch reports, so a notch always carries the list on by
       one and never leaves a remainder behind to shorten the next one. Trackpads
       report far smaller steps and so still feel the fill. */
    const DWELL = 80;
    /* One wheel notch in line mode, and Firefox reports lines rather than
       pixels. Its own default is three lines to a notch. */
    const LINE_HEIGHT = 36;

    let held = 0;
    let frame = null;
    let settle = null;
    let settleFrame = null;
    let settleStart = null;
    let settleFrom = 0;
    let settleTo = 0;
    let settleAt = 0;
    let lastScrollTop = scrollport.scrollTop;
    let direction = 1;

    const nav = document.querySelector('.gh-profile-nav');
    const sections = Array.from(
        document.querySelectorAll('.gh-cv-sections .gh-cv-section')
    );
    /* Which group each entry belongs to, resolved once. The label only ever
       changes where the reader crosses out of one group into the next, so
       walking down a group leaves the rail alone. */
    const sectionOf = entries.map(function (entry) {
        return sections.indexOf(entry.closest('.gh-cv-section'));
    });

    let spans = null;
    let shown = null;
    let hovered = null;
    let onContent = false;
    let scrolled = false;
    let focused = null;

    /* The group headings sit in the content as real h2s and are hidden there, so
       the document keeps its structure and assistive technology reads each group
       in place. What the rail shows is a mirror of them and stays out of the
       accessibility tree. One span per group, stacked by the stylesheet, so a
       change from one to the next cross-fades rather than dipping through an
       empty rail on the way. */
    function buildLabel() {
        if (!nav || !sections.length) return;

        const texts = sections.map(function (section) {
            const heading = section.querySelector('h2');

            return heading ? heading.textContent.trim() : '';
        });

        if (!texts.some(Boolean)) return;

        const label = document.createElement('p');

        label.className = 'gh-cv-rail-label';
        label.setAttribute('aria-hidden', 'true');

        spans = texts.map(function (text) {
            const span = document.createElement('span');

            span.textContent = text;
            label.appendChild(span);

            return span;
        });

        nav.appendChild(label);
    }

    /* Reading is what asks for a label: the pointer resting on the content, or
       the page being scrolled, whether or not the pointer is anywhere near it.
       Taking the pointer off the content — to the rail, the header, the space
       above the page — puts the navigation back and drops the scrolling with
       it, so leaving really does leave.

       What it names is the group the reading line is in, never the one under
       the pointer. The pointer used to win, and reading down the page across a
       group boundary then renamed the rail while the black entry beside it
       still belonged to the group before — the label pointing at one group and
       the entry it sits next to belonging to another. The pointer asks for the
       label; the reading line answers. Only where there is no reading line at
       all — reduced motion, where nothing shades and focused stays null — does
       the pointer answer for itself. */
    function updateLabel() {
        if (!spans) return;

        let index = null;

        if (onContent || scrolled) {
            const entry = focused !== null ? focused : hovered;

            if (entry !== null) index = sectionOf[entry];
        }

        /* An entry outside any group has nothing to name. */
        if (index === undefined || index < 0) index = null;
        if (index === shown) return;

        if (shown !== null) spans[shown].classList.remove('is-current');
        if (index !== null) spans[index].classList.add('is-current');

        nav.classList.toggle('is-labelled', index !== null);
        shown = index;
    }

    function bindLabel() {
        if (!spans) return;

        const panel = document.querySelector('.gh-profile-panel');
        const list = entries[0].closest('.gh-cv-sections');

        if (!panel) return;

        /* The column the CV reads in, not the list alone: crossing a gap between
           two groups should not put the navigation back for the moment it takes
           to pass. */
        panel.addEventListener('pointerenter', function () {
            onContent = true;
            updateLabel();
        });

        panel.addEventListener('pointerleave', function () {
            onContent = false;
            scrolled = false;
            updateLabel();
        });

        /* Delegated, so there is one listener rather than one per row. */
        list.addEventListener('pointerover', function (event) {
            const entry = event.target.closest('.gh-cv-entry');
            const index = entry ? entries.indexOf(entry) : -1;

            hovered = index < 0 ? null : index;
            updateLabel();
        });

        list.addEventListener('pointerleave', function () {
            hovered = null;
            updateLabel();
        });
    }

    /* Where an entry's first line sits inside its box: its half-line of padding,
       and half a line down into the line itself. */
    let firstLine = 0;

    /* Read off the list rather than written down here, so it follows the
       stylesheet across the breakpoints instead of agreeing with it at one. */
    function measureFirstLine() {
        const style = getComputedStyle(entries[0]);
        const line = parseFloat(style.lineHeight);

        firstLine = Number.isFinite(line)
            ? (parseFloat(style.paddingTop) || 0) + line / 2
            : entries[0].getBoundingClientRect().height / 2;
    }

    /* The first line of the entry, not the middle of its box. An entry that wraps
       or carries a note under it is taller than one line, and its box centre sits
       half a line lower for every line it holds — so anchoring there lands a long
       entry that much below the rail beside it, and by a different amount for each
       one. Every entry's first line falls on the rail's grid, whatever it holds. */
    function anchorOf(entry) {
        return entry.getBoundingClientRect().top + firstLine;
    }

    /* Where the line rests on screen: the first entry's first line before any
       scrolling, which is the position the scroll then carries every entry
       through. Adding the scroll offset back to the current anchor recovers it
       at any scroll position. */
    function lineFor(anchors) {
        return anchors[0] + scrollport.scrollTop;
    }

    function nearestTo(anchors, line) {
        return anchors.reduce(function (nearest, anchor) {
            return Math.abs(anchor - line) < Math.abs(nearest - line)
                ? anchor
                : nearest;
        }, anchors[0]);
    }

    /* Only an entry sitting on the line reaches full strength, and a wheel notch
       and the row pitch have no reason to agree, so the scroll rarely comes to
       rest on one by itself. Rather than anchoring the shading on the nearest
       entry, which makes the whole list step a row at a time as the scroll
       crosses between two, the scroll itself is nudged the rest of the way once
       the input stops. Nothing is fought while scrolling, and the list settles
       with an entry black. */
    function settleOnEntry() {
        settle = null;

        if (settleFrame !== null) return;

        const anchors = entries.map(anchorOf);
        const line = lineFor(anchors);

        if (Math.abs(nearestTo(anchors, line) - line) < SETTLE_EPSILON) return;

        /* The entry ahead in the direction of travel, never the one already
           passed: carrying on the way the reader was going reads as the list
           coming to rest, where turning back reads as the scroll being taken
           away from them. Only at the ends of the list, where there is nothing
           ahead, does it fall back on the nearest. */
        const ahead = anchors.filter(function (anchor) {
            return direction > 0 ? anchor > line : anchor < line;
        });
        const target = ahead.length
            ? (direction > 0 ? ahead[0] : ahead[ahead.length - 1])
            : nearestTo(anchors, line);

        runTo(target - line);
    }

    function runTo(delta) {
        settleFrom = scrollport.scrollTop;
        settleTo = settleFrom + delta;
        settleStart = null;
        settleFrame = requestAnimationFrame(stepSettle);
    }

    /* The wheel does not scroll the list, it fills up against the entry the line
       is on; only once it is full does the list move, and then by exactly one
       entry. So each entry holds the reader for longer than a page would, the
       move between two is short, and it runs to the next entry whether or not
       the scrolling that started it is still going. Nothing is left resting
       between two entries, which is what the settle was there to repair.

       Wheel only. Touch, the scrollbar and the keyboard scroll as they always
       did, and the settle catches them where they come to rest. */
    function onWheel(event) {
        /* Zoom, not scroll. */
        if (event.ctrlKey) return;

        event.preventDefault();

        /* Mid-move: the rest of the notch belongs to the move being finished. */
        if (settleFrame !== null) return;

        const step =
            event.deltaMode === 1
                ? event.deltaY * LINE_HEIGHT
                : event.deltaMode === 2
                  ? event.deltaY * scrollport.clientHeight
                  : event.deltaY;

        /* A turn starts the count over rather than unwinding what was held. */
        if (held !== 0 && Math.sign(step) !== Math.sign(held)) held = 0;

        held += step;

        if (Math.abs(held) < DWELL) return;

        const anchors = entries.map(anchorOf);
        const line = lineFor(anchors);
        const index = anchors.indexOf(nearestTo(anchors, line));
        const next = anchors[index + (held > 0 ? 1 : -1)];

        held = 0;

        /* Nothing beyond the ends of the list to move on to. */
        if (next === undefined) return;

        runTo(next - line);
    }

    function stepSettle(now) {
        /* The scroll has moved without us, so the reader has taken it back. */
        if (settleStart !== null && Math.abs(scrollport.scrollTop - settleAt) > 2) {
            settleFrame = null;
            return;
        }

        if (settleStart === null) settleStart = now;

        const t = Math.min((now - settleStart) / SETTLE_DURATION, 1);
        /* Smoothstep, so neither end of the run has a kick in it. */
        const eased = t * t * (3 - 2 * t);

        scrollport.scrollTop = settleFrom + (settleTo - settleFrom) * eased;
        /* Read back rather than kept, so the clamp at either end of the scroll
           range does not read as the reader taking over on the next frame. */
        settleAt = scrollport.scrollTop;
        settleFrame = t < 1 ? requestAnimationFrame(stepSettle) : null;
    }

    function paint() {
        frame = null;

        /* Intersect the scrollport with the window, so the same maths holds whether
           the scrollport is the scrolling box or the whole page scrolls past it. */
        const box = scrollport.getBoundingClientRect();
        const top = Math.max(box.top, 0);
        const height = Math.min(box.bottom, window.innerHeight) - top;

        if (height <= 0) return;

        /* The line itself, so the shading tracks the scroll exactly and no entry
           changes strength faster than the scroll moves it. Held at the last
           entry, which the scroll can otherwise run out before reaching. */
        const anchors = entries.map(anchorOf);
        const focusY = Math.min(lineFor(anchors), anchors[anchors.length - 1]);
        const fade = height * FADE_RATIO;
        const bottom = top + height;
        /* Never reaching past the focus, so the entry the line rests on keeps its
           full strength however short the viewport is. */
        const topEdge = Math.min(height * EDGE_RATIO, focusY - top);
        const bottomEdge = Math.min(height * EDGE_RATIO, bottom - focusY);

        function dissolve(anchor) {
            const above = topEdge > 0 ? (anchor - top) / topEdge : 1;
            const below = bottomEdge > 0 ? (bottom - anchor) / bottomEdge : 1;

            return Math.min(Math.max(Math.min(above, below), 0), 1);
        }

        /* Straight off the distance to the focus, so the list still shades
           continuously with the scroll rather than stepping row by row. */
        entries.forEach(function (entry, index) {
            const ratio = Math.min(Math.abs(anchors[index] - focusY) / fade, 1);
            /* Steepest at the line and flattening out after, so the neighbouring
               entry drops away rather than reading as a second foreground row. */
            const eased = 1 - (1 - ratio) * (1 - ratio) * (1 - ratio);
            const shade = 1 - eased * (1 - FADED_OPACITY);

            entry.style.opacity = (shade * dissolve(anchors[index])).toFixed(3);
        });

        focused = anchors.indexOf(nearestTo(anchors, focusY));
        updateLabel();
    }

    function schedule() {
        if (scrollport.scrollTop !== lastScrollTop) {
            direction = scrollport.scrollTop > lastScrollTop ? 1 : -1;
            lastScrollTop = scrollport.scrollTop;
            /* Moved by the wheel, the scrollbar or the keyboard: the reader is
               in the list whether or not the pointer says so. */
            scrolled = true;
        }

        clearTimeout(settle);
        settle = setTimeout(settleOnEntry, SETTLE_DELAY);

        if (frame === null) {
            frame = requestAnimationFrame(paint);
        }
    }

    buildLabel();
    bindLabel();
    measureFirstLine();

    /* Everything past here moves something: the shading, the dwell the wheel
       fills against an entry, and the settle. None of it runs under reduced
       motion, and with no reading line to point at, the rail label answers the
       pointer alone. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* Capture phase: scroll does not bubble, and the scrolling box differs between
       the desktop scrollport and a page that scrolls on its own. */
    /* Not passive: the whole point is that the wheel does not scroll the list. */
    scrollport.addEventListener('wheel', onWheel, {passive: false});
    document.addEventListener('scroll', schedule, {passive: true, capture: true});
    /* The breakpoint carries its own padding and type size with it, so the line
       is measured again before anything is read off it. */
    window.addEventListener('resize', function () {
        measureFirstLine();
        schedule();
    });
    window.addEventListener('pageshow', schedule);

    paint();
}
